import React from 'react';
import { SPORT_TO_LEAGUE_ID } from '@low6-pickem/sports-config';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Activity, Rss } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import type { Question } from '@/api/questions/getQuestions';

interface LiveScoringQuestionHeaderProps {
  question: Question;
  questionNumber: number;
  status: 'LIVE' | 'FINAL' | 'SCHEDULED';
  points: number;
  manualScoring?: boolean;
  onManualScoringChange?: (enabled: boolean) => void;
  hasOverrides?: boolean;
  onViewFeedStats?: () => void;
}

export function LiveScoringQuestionHeader({
  question,
  questionNumber,
  status,
  points,
  manualScoring: controlledManualScoring,
  onManualScoringChange,
  hasOverrides = false,
  onViewFeedStats,
}: LiveScoringQuestionHeaderProps) {
  const navigate = useNavigate();
  const [internalManualScoring, setInternalManualScoring] = React.useState(false);
  const manualScoring = controlledManualScoring ?? internalManualScoring;

  // Custom questions only support manual scoring - no toggle
  const isCustom = (question.question_type as string) === 'custom';

  // Custom questions and questions without fixtures don't have match stats
  const hasFixture = !isCustom && !!question.fixture_external_id;

  const handleManualScoringChange = (enabled: boolean) => {
    if (onManualScoringChange) {
      onManualScoringChange(enabled);
    } else {
      setInternalManualScoring(enabled);
    }
  };

  return (
    <div className="flex flex-col gap-2 pb-3 border-b">
      {/* Row 1: Question text full width */}
      <span className="text-base font-medium">
        {questionNumber}.{' '}
        {question.question_text_en || question.question_text_fr || 'Untitled Question'}
      </span>

      {/* Row 2: All controls */}
      <div className="flex items-center gap-3">
        {hasFixture && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={onViewFeedStats}
            className="h-8"
          >
            <Rss className="h-4 w-4 mr-1.5" />
            View Feed
          </Button>
        )}

        {hasFixture && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => {
              const sportId = question.sport_external_id || 'nfl';
              // Sportradar-specific IDs need manual mapping; plain codes use SPORT_TO_LEAGUE_ID
              const srOverrides: Record<string, string> = {
                'sr:sport:1': 'nfl',
                'sr:sport:2': 'nba',
              };
              const sportSlug =
                srOverrides[sportId] ||
                SPORT_TO_LEAGUE_ID[sportId.toUpperCase()] ||
                sportId.toLowerCase() ||
                'nfl';

              navigate({
                to: '/dashboard/sports/$id/matches/$matchId/stats',
                params: {
                  id: sportSlug,
                  matchId: question.fixture_external_id!,
                },
              });
            }}
            className="h-8"
          >
            <Activity className="h-4 w-4 mr-1.5" />
            Match Stats
          </Button>
        )}

        <div className="flex-1" />

        {isCustom ? (
          <span className="text-sm text-muted-foreground">Manual only</span>
        ) : (
          <div className="flex items-center gap-2">
            <Switch
              id={`manual-scoring-${question.question_id}`}
              checked={manualScoring}
              onCheckedChange={handleManualScoringChange}
              className="scale-90"
            />
            <Label
              htmlFor={`manual-scoring-${question.question_id}`}
              className="text-sm font-normal cursor-pointer"
            >
              Manual Scoring
            </Label>
          </div>
        )}

        <Badge variant="outline" className="text-sm font-normal">
          {points}pts
          {(question.question_metadata as { bonus_type?: string } | null)?.bonus_type ===
          'per_option'
            ? '/per'
            : ''}
        </Badge>

        <Badge
          className={`text-sm font-normal min-w-[110px] justify-center ${
            status === 'FINAL'
              ? 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
              : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
          }`}
        >
          {status}
        </Badge>

        {hasOverrides && (
          <Badge
            variant="outline"
            className="text-sm font-normal bg-amber-50 text-amber-700 border-amber-300 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-700"
          >
            🔧 Override
          </Badge>
        )}
      </div>
    </div>
  );
}
